import type { FC, ReactNode } from 'react';
import { Circle, G, Path, Rect, Svg } from '../brand/primitives';
import type { UIIconComponent, UIIconProps } from './types';

const make = (name: string, body: ReactNode): UIIconComponent => {
  const Component: FC<UIIconProps> = ({ size = 20, color = 'currentColor', strokeWidth = 1.6 }) => (
    <Svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {body}
    </Svg>
  );
  Component.displayName = name;
  return Component;
};

// ─── Navigation ───────────────────────────────────────────────────────────
export const HomeIcon = make(
  'HomeIcon',
  <G>
    <Path d="M3 12 L12 3 L21 12 V21 H3 Z" />
    <Path d="M9 21 V14 H15 V21" />
  </G>,
);
export const MapIcon = make(
  'MapIcon',
  <G>
    <Path d="M2 6 L9 3 L15 6 L22 3 V18 L15 21 L9 18 L2 21 Z" />
    <Path d="M9 3 V18 M15 6 V21" />
  </G>,
);
export const SearchIcon = make(
  'SearchIcon',
  <G>
    <Circle cx={10} cy={10} r={6} />
    <Path d="M14.5 14.5 L21 21" />
  </G>,
);
export const BellIcon = make(
  'BellIcon',
  <G>
    <Path d="M6 16 V11 a6 6 0 1 1 12 0 V16 L20 19 H4 Z" />
    <Path d="M10 22 a2 2 0 1 0 4 0" />
  </G>,
);
export const QrIcon = make(
  'QrIcon',
  <G>
    <Rect x={3} y={3} width={7} height={7} rx={1} />
    <Rect x={14} y={3} width={7} height={7} rx={1} />
    <Rect x={3} y={14} width={7} height={7} rx={1} />
    <Path d="M14 14 H17 V17 H14Z M19 14 V17 M14 19 H17 V21 H19V19" />
  </G>,
);
export const CheckIcon = make('CheckIcon', <Path d="M4 12 L10 18 L20 6" />);
export const CalendarIcon = make(
  'CalendarIcon',
  <G>
    <Rect x={3} y={5} width={18} height={16} rx={2} />
    <Path d="M3 10 H21 M8 3 V7 M16 3 V7" />
  </G>,
);
export const UserIcon = make(
  'UserIcon',
  <G>
    <Circle cx={12} cy={8} r={4} />
    <Path d="M4 21 a8 8 0 0 1 16 0" />
  </G>,
);
export const ChatIcon = make(
  'ChatIcon',
  <G>
    <Path d="M3 5 H21 V17 H12 L7 21 V17 H3 Z" />
  </G>,
);
export const CardIcon = make(
  'CardIcon',
  <G>
    <Rect x={3} y={5} width={18} height={14} rx={2} />
    <Path d="M3 10 H21" />
  </G>,
);
export const CupIcon = make(
  'CupIcon',
  <G>
    <Path d="M7 4 H17 V11 a5 5 0 0 1 -10 0 Z" />
    <Path d="M17 6 H21 V9 A4 4 0 0 1 17 11 M7 6 H3 V9 A4 4 0 0 0 7 11" />
    <Path d="M9 17 H15 V21 H9 Z" />
  </G>,
);
export const PlusIcon = make('PlusIcon', <Path d="M12 5 V19 M5 12 H19" />);
export const SettingsIcon = make(
  'SettingsIcon',
  <G>
    <Circle cx={12} cy={12} r={3} />
    <Path d="M12 2v3M12 19v3M22 12h-3M5 12H2M19 5l-2 2M7 17l-2 2M19 19l-2-2M7 7L5 5" />
  </G>,
);
export const PinIcon = make(
  'PinIcon',
  <G>
    <Path d="M12 21 C 7 16 5 12 5 9 a 7 7 0 0 1 14 0 c 0 3 -2 7 -7 12 Z" />
    <Circle cx={12} cy={9} r={2.5} />
  </G>,
);
export const ArrowIcon = make(
  'ArrowIcon',
  <G>
    <Path d="M5 12 H19 M13 6 L19 12 L13 18" />
  </G>,
);

// ─── Directional ──────────────────────────────────────────────────────────
export const BackIcon = make('BackIcon', <Path d="M19 12 H5 M11 6 L5 12 L11 18" />);
export const ForwardIcon = make('ForwardIcon', <Path d="M5 12 H19 M13 6 L19 12 L13 18" />);
export const UpIcon = make('UpIcon', <Path d="M12 19 V5 M6 11 L12 5 L18 11" />);
export const DownIcon = make('DownIcon', <Path d="M12 5 V19 M6 13 L12 19 L18 13" />);
export const ChevronRightIcon = make('ChevronRightIcon', <Path d="M9 5 L16 12 L9 19" />);
export const ChevronLeftIcon = make('ChevronLeftIcon', <Path d="M15 5 L8 12 L15 19" />);
export const ChevronDownIcon = make('ChevronDownIcon', <Path d="M5 9 L12 16 L19 9" />);

// ─── Controls / actions ──────────────────────────────────────────────────
export const CloseIcon = make('CloseIcon', <Path d="M6 6 L18 18 M18 6 L6 18" />);
export const MenuIcon = make('MenuIcon', <Path d="M3 6 H21 M3 12 H21 M3 18 H21" />);
export const MoreIcon: UIIconComponent = ({ size = 20, color = 'currentColor' }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Circle cx={5} cy={12} r={1.5} fill={color} />
    <Circle cx={12} cy={12} r={1.5} fill={color} />
    <Circle cx={19} cy={12} r={1.5} fill={color} />
  </Svg>
);
MoreIcon.displayName = 'MoreIcon';

export const FilterIcon = make('FilterIcon', <Path d="M3 5 H21 L14 13 V20 L10 18 V13 Z" />);
export const SortIcon = make(
  'SortIcon',
  <G>
    <Path d="M7 4 V20 M3 8 L7 4 L11 8" />
    <Path d="M17 4 V20 M13 16 L17 20 L21 16" />
  </G>,
);

// ─── Appearance ─────────────────────────────────────────────────────────
export const AppearanceIcon: UIIconComponent = ({
  size = 20,
  color = 'currentColor',
  strokeWidth = 1.6,
}) => (
  <Svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <Circle cx={12} cy={12} r={9} />
    <Path d="M12 3a9 9 0 0 1 0 18z" fill={color} stroke="none" />
  </Svg>
);
AppearanceIcon.displayName = 'AppearanceIcon';

export const SunIcon = make(
  'SunIcon',
  <G>
    <Circle cx={12} cy={12} r={4} />
    <Path d="M12 2 V4 M12 20 V22 M2 12 H4 M20 12 H22 M5 5 L6.5 6.5 M17.5 17.5 L19 19 M5 19 L6.5 17.5 M17.5 6.5 L19 5" />
  </G>,
);
export const MoonIcon = make(
  'MoonIcon',
  <Path d="M21 12.8 A9 9 0 1 1 11.2 3 A7 7 0 0 0 21 12.8 Z" />,
);
export const PaletteIcon = make(
  'PaletteIcon',
  <G>
    <Path d="M12 3 A9 9 0 1 0 12 21 H13 A2 2 0 0 0 14.6 17.8 A2 2 0 0 1 16.2 14.6 H18 A3 3 0 0 0 21 11.6 C21 6.8 17 3 12 3 Z" />
    <Circle cx={7} cy={10} r={1} />
    <Circle cx={10} cy={7} r={1} />
    <Circle cx={14} cy={7} r={1} />
    <Circle cx={17} cy={10} r={1} />
  </G>,
);

// ─── Affinity ────────────────────────────────────────────────────────────
export const HeartIcon = make(
  'HeartIcon',
  <Path d="M12 21 C 4 14 4 8 8 6 C 11 4.5 12 7 12 7 C 12 7 13 4.5 16 6 C 20 8 20 14 12 21 Z" />,
);
export const StarIcon = make(
  'StarIcon',
  <Path d="M12 3 L14.5 9 L21 10 L16 14.5 L17.5 21 L12 17.5 L6.5 21 L8 14.5 L3 10 L9.5 9 Z" />,
);
export const BookmarkIcon = make('BookmarkIcon', <Path d="M6 3 H18 V21 L12 17 L6 21 Z" />);

// ─── Commerce / money ────────────────────────────────────────────────────
export const TicketIcon = make(
  'TicketIcon',
  <Path d="M3 8 V11 a2 2 0 1 1 0 2 V16 H21 V13 a2 2 0 1 1 0 -2 V8 Z" />,
);
export const WalletIcon: UIIconComponent = ({
  size = 20,
  color = 'currentColor',
  strokeWidth = 1.6,
}) => (
  <Svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <Rect x={3} y={6} width={18} height={14} rx={2} />
    <Path d="M16 13 H21 M3 10 V6 a1 1 0 0 1 1 -1 H17" />
    <Circle cx={16} cy={13} r={1.4} fill={color} stroke="none" />
  </Svg>
);
WalletIcon.displayName = 'WalletIcon';

export const ReceiptIcon = make(
  'ReceiptIcon',
  <Path d="M6 3 H18 V21 L16 19 L13.5 21 L11 19 L8.5 21 L6 19 Z M9 8 H15 M9 12 H15 M9 16 H12" />,
);

// ─── Transfer ────────────────────────────────────────────────────────────
export const UploadIcon = make('UploadIcon', <Path d="M12 4 V16 M6 10 L12 4 L18 10 M4 20 H20" />);
export const DownloadIcon = make(
  'DownloadIcon',
  <Path d="M12 4 V16 M6 12 L12 18 L18 12 M4 20 H20" />,
);
export const ShareIcon = make(
  'ShareIcon',
  <G>
    <Circle cx={6} cy={12} r={2.5} />
    <Circle cx={18} cy={6} r={2.5} />
    <Circle cx={18} cy={18} r={2.5} />
    <Path d="M8 11 L16 7 M8 13 L16 17" />
  </G>,
);
export const CopyIcon = make(
  'CopyIcon',
  <G>
    <Rect x={8} y={3} width={13} height={13} rx={2} />
    <Rect x={3} y={8} width={13} height={13} rx={2} />
  </G>,
);

// ─── Sport / community ──────────────────────────────────────────────────
export const WhistleIcon = make(
  'WhistleIcon',
  <G>
    <Circle cx={9} cy={14} r={6} />
    <Path d="M14 11 L20 7 L21 9 L15 13 M9 14 L11 9" />
  </G>,
);
export const TrophyIcon = make(
  'TrophyIcon',
  <G>
    <Path d="M8 4 H16 V11 a4 4 0 0 1 -8 0 Z" />
    <Path d="M16 6 H19 V9 A3 3 0 0 1 16 11 M8 6 H5 V9 A3 3 0 0 0 8 11" />
    <Path d="M10 17 H14 V21 H10 Z M7 21 H17" />
  </G>,
);
export const TeamIcon = make(
  'TeamIcon',
  <G>
    <Circle cx={8} cy={9} r={3} />
    <Circle cx={16} cy={9} r={3} />
    <Path d="M3 19 a5 5 0 0 1 10 0 M11 19 a5 5 0 0 1 10 0" />
  </G>,
);
export const FlagIcon = make(
  'FlagIcon',
  <G>
    <Path d="M5 3 V21 M5 4 H17 L15 9 L17 14 H5" />
  </G>,
);

// ─── Visibility / state ──────────────────────────────────────────────────
export const EyeIcon = make(
  'EyeIcon',
  <G>
    <Path d="M2 12 C 5 6 9 4 12 4 C 15 4 19 6 22 12 C 19 18 15 20 12 20 C 9 20 5 18 2 12 Z" />
    <Circle cx={12} cy={12} r={3} />
  </G>,
);
export const EyeOffIcon = make(
  'EyeOffIcon',
  <G>
    <Path d="M3 3 L21 21" />
    <Path d="M6.5 7 C 4.5 8.5 3 10 2 12 C 5 18 9 20 12 20 C 14 20 16 19.5 17.5 18.5 M11 6 C 11.3 5.9 11.7 4 12 4 C 15 4 19 6 22 12 C 21.3 13.3 20.5 14.4 19.8 15.3" />
  </G>,
);
export const EditIcon = make('EditIcon', <Path d="M4 20 L4 16 L16 4 L20 8 L8 20 Z M14 6 L18 10" />);
export const TrashIcon = make(
  'TrashIcon',
  <G>
    <Path d="M4 6 H20 M9 6 V4 H15 V6 M6 6 L7 20 H17 L18 6" />
    <Path d="M10 11 V17 M14 11 V17" />
  </G>,
);

// ─── Security ────────────────────────────────────────────────────────────
export const LockIcon = make(
  'LockIcon',
  <G>
    <Rect x={5} y={11} width={14} height={10} rx={1} />
    <Path d="M8 11 V7 a4 4 0 0 1 8 0 V11" />
  </G>,
);
export const ShieldIcon = make(
  'ShieldIcon',
  <Path d="M12 3 L20 6 V11 C 20 16 16 20 12 21 C 8 20 4 16 4 11 V6 Z" />,
);

// ─── Locale / device ─────────────────────────────────────────────────────
export const GlobeIcon = make(
  'GlobeIcon',
  <G>
    <Circle cx={12} cy={12} r={9} />
    <Path d="M3 12 H21 M12 3 C 8 7 8 17 12 21 C 16 17 16 7 12 3" />
  </G>,
);
export const LanguageIcon = make(
  'LanguageIcon',
  <G>
    <Path d="M3 7 H13 M8 4 V7 M5 18 C 6 12 8 7 8 7 C 8 7 10 12 11 18 M5 14 H11" />
    <Path d="M13 21 C 15 19 19 19 21 21 M21 11 V16 H16" />
  </G>,
);
export const HapticIcon = make(
  'HapticIcon',
  <G>
    <Circle cx={12} cy={12} r={3} />
    <Path d="M8 12 a4 4 0 0 1 8 0 M6 12 a6 6 0 0 1 12 0 M4 12 a8 8 0 0 1 16 0" />
  </G>,
);
export const BraveMoveGlyphIcon = make(
  'BraveMoveGlyphIcon',
  <Path d="M3 12 H7 L9 5 L13 19 L16 9 L18 12 L21 12" />,
);

// ─── Registry (dynamic-name lookup) ───────────────────────────────────────

export const UI_REGISTRY = {
  // base
  home: HomeIcon,
  map: MapIcon,
  search: SearchIcon,
  bell: BellIcon,
  qr: QrIcon,
  check: CheckIcon,
  calendar: CalendarIcon,
  user: UserIcon,
  chat: ChatIcon,
  card: CardIcon,
  cup: CupIcon,
  plus: PlusIcon,
  settings: SettingsIcon,
  pin: PinIcon,
  arrow: ArrowIcon,
  // directional
  back: BackIcon,
  forward: ForwardIcon,
  up: UpIcon,
  down: DownIcon,
  chevronRight: ChevronRightIcon,
  chevronLeft: ChevronLeftIcon,
  chevronDown: ChevronDownIcon,
  // controls / actions
  close: CloseIcon,
  menu: MenuIcon,
  more: MoreIcon,
  filter: FilterIcon,
  sort: SortIcon,
  // appearance
  appearance: AppearanceIcon,
  sun: SunIcon,
  moon: MoonIcon,
  palette: PaletteIcon,
  // affinity
  heart: HeartIcon,
  star: StarIcon,
  bookmark: BookmarkIcon,
  // commerce
  ticket: TicketIcon,
  wallet: WalletIcon,
  receipt: ReceiptIcon,
  // transfer
  upload: UploadIcon,
  download: DownloadIcon,
  share: ShareIcon,
  copy: CopyIcon,
  // community / sport
  whistle: WhistleIcon,
  trophy: TrophyIcon,
  team: TeamIcon,
  flag: FlagIcon,
  // visibility / state
  eye: EyeIcon,
  eyeOff: EyeOffIcon,
  edit: EditIcon,
  trash: TrashIcon,
  // security
  lock: LockIcon,
  shield: ShieldIcon,
  // locale / device
  globe: GlobeIcon,
  language: LanguageIcon,
  haptic: HapticIcon,
  pulse: BraveMoveGlyphIcon,
} as const satisfies Record<string, UIIconComponent>;

export type UIIconName = keyof typeof UI_REGISTRY;
export const UI_ICON_NAMES: readonly UIIconName[] = Object.keys(UI_REGISTRY) as UIIconName[];

export interface UIIconWrapperProps extends UIIconProps {
  readonly name: UIIconName;
}

export const UIIcon: FC<UIIconWrapperProps> = ({ name, ...rest }) => {
  const Component = UI_REGISTRY[name];
  if (!Component) return null;
  return <Component {...rest} />;
};
